import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface TutorialStep {
  id: string;
  title: string;
  description: string;
  targetSelector: string;
  position: 'top' | 'bottom' | 'left' | 'right';
}

@Injectable({
  providedIn: 'root',
})
export class TutorialService {
  private readonly TUTORIAL_COMPLETED_KEY = 'markado-tutorial-completed';
  private readonly TUTORIAL_SKIPPED_KEY = 'markado-tutorial-skipped';

  private isActiveSubject = new BehaviorSubject<boolean>(false);
  private currentStepSubject = new BehaviorSubject<number>(-1);
  private stepsSubject = new BehaviorSubject<TutorialStep[]>([]);

  public isActive$ = this.isActiveSubject.asObservable();
  public currentStep$ = this.currentStepSubject.asObservable();
  public steps$ = this.stepsSubject.asObservable();

  private tutorialSteps: TutorialStep[] = [
    {
      id: 'welcome',
      title: 'AI-Powered Features',
      description:
        'Markado includes advanced AI tools to enhance your browsing and search experience.',
      targetSelector: '',
      position: 'bottom',
    },
    {
      id: 'reverse-image-search',
      title: 'AI Image Search',
      description:
        'Upload any image to find similar listings on Markado. The AI analyzes visual features to match items across categories.',
      targetSelector: '.reverse-search-button',
      position: 'bottom',
    },
    {
      id: 'chat-assistant',
      title: 'AI Assistant',
      description:
        'Ask questions in natural language to search listings, get recommendations, or navigate the platform efficiently.',
      targetSelector: '.floating-chat-button',
      position: 'left',
    },
    {
      id: 'complete',
      title: 'Feature Overview Complete',
      description:
        'These AI features are available throughout your Markado experience to help you find exactly what you need.',
      targetSelector: '',
      position: 'bottom',
    },
  ];

  constructor() {}

  shouldShowTutorial(): boolean {
    try {
      const completed = localStorage.getItem(this.TUTORIAL_COMPLETED_KEY);
      const skipped = localStorage.getItem(this.TUTORIAL_SKIPPED_KEY);
      return !completed && !skipped;
    } catch (error) {
      console.warn('Failed to check tutorial status:', error);
      return true;
    }
  }

  startTutorial(): void {
    this.stepsSubject.next(this.tutorialSteps);
    this.currentStepSubject.next(0);
    this.isActiveSubject.next(true);
    document.body.style.overflow = 'hidden';
    document.body.classList.add('tutorial-mode');
  }

  skipTutorial(): void {
    try {
      localStorage.setItem(this.TUTORIAL_SKIPPED_KEY, 'true');
    } catch (error) {
      console.warn('Failed to save tutorial skip status:', error);
    }
    this.endTutorial();
  }

  nextStep(): void {
    const currentStep = this.currentStepSubject.value;
    const steps = this.stepsSubject.value;

    if (currentStep < steps.length - 1) {
      this.currentStepSubject.next(currentStep + 1);
    } else {
      this.completeTutorial();
    }
  }

  previousStep(): void {
    const currentStep = this.currentStepSubject.value;
    if (currentStep > 0) {
      this.currentStepSubject.next(currentStep - 1);
    }
  }

  completeTutorial(): void {
    try {
      localStorage.setItem(this.TUTORIAL_COMPLETED_KEY, 'true');
    } catch (error) {
      console.warn('Failed to save tutorial completion status:', error);
    }
    this.endTutorial();
  }

  private endTutorial(): void {
    this.isActiveSubject.next(false);
    this.currentStepSubject.next(-1);
    document.body.style.overflow = '';
    document.body.classList.remove('tutorial-mode');
  }

  getCurrentStep(): TutorialStep | null {
    const currentStepIndex = this.currentStepSubject.value;
    const steps = this.stepsSubject.value;

    if (currentStepIndex >= 0 && currentStepIndex < steps.length) {
      return steps[currentStepIndex];
    }

    return null;
  }

  isStepActive(stepId: string): boolean {
    const currentStep = this.getCurrentStep();
    return currentStep?.id === stepId;
  }

  getTotalSteps(): number {
    return this.tutorialSteps.length;
  }

  getCurrentStepNumber(): number {
    return this.currentStepSubject.value + 1;
  }

  resetTutorial(): void {
    try {
      localStorage.removeItem(this.TUTORIAL_COMPLETED_KEY);
      localStorage.removeItem(this.TUTORIAL_SKIPPED_KEY);
    } catch (error) {
      console.warn('Failed to reset tutorial status:', error);
    }
  }
}
